import { orderBy } from 'lodash-es'

// Two letters, six digits, one letter (EN967441H). The order-level Lab
// Reference ID is seven digits and never matches.
const QUEST_ACCESSION_PATTERN = /^[A-Z]{2}\d{6}[A-Z]$/

export function parseQuestAccession(
  value: string | null | undefined
): string | undefined {
  const accession = value?.trim().toUpperCase()
  return accession && QUEST_ACCESSION_PATTERN.test(accession)
    ? accession
    : undefined
}

export const LabCopyKind = {
  // Quest's own PDF, passed through by Health Gorilla.
  QUEST_PDF: 'quest-pdf',
  // Health Gorilla's PDF rendering of the structured report.
  HEALTH_GORILLA_REPORT: 'health-gorilla-report',
  ELATION: 'elation',
} as const
export type LabCopyKind = (typeof LabCopyKind)[keyof typeof LabCopyKind]

export const LAB_COPY_KIND_LABELS: Record<LabCopyKind, string> = {
  [LabCopyKind.QUEST_PDF]: 'Quest PDF',
  [LabCopyKind.HEALTH_GORILLA_REPORT]: 'Health Gorilla report',
  [LabCopyKind.ELATION]: 'Elation',
}

// Lower shows first. Quest's PDF is the originating party's document, Health
// Gorilla's rendering is a relay of the structured data, Elation's is a second
// delivery of the same result.
const LAB_COPY_PRIORITY: Record<LabCopyKind, number> = {
  [LabCopyKind.QUEST_PDF]: 0,
  [LabCopyKind.HEALTH_GORILLA_REPORT]: 1,
  [LabCopyKind.ELATION]: 2,
}

export interface LabCopyMember {
  // DocumentReference/<id>
  reference: string
  kind: LabCopyKind
  received: string
  hasOpenReviewTask: boolean
  // Id of the folder the document is filed in, if any. Folders limit who can
  // read a document.
  folder: string | undefined
}

export interface LabCopyState {
  group: string | undefined
  // Reference string of the primary copy; set on every shown-elsewhere copy.
  copyOf: string | undefined
  // Would be a copy of the primary but stays its own row because a review task
  // is open on it. The marker lets the sweep find it once the task closes.
  pinned: boolean
}

export const NO_LAB_COPY_GROUP: LabCopyState = {
  group: undefined,
  copyOf: undefined,
  pinned: false,
}

function receivedAt(received: string): number {
  const time = Date.parse(received)
  return Number.isNaN(time) ? Number.POSITIVE_INFINITY : time
}

export function choosePrimaryLabCopy(
  members: readonly LabCopyMember[]
): LabCopyMember | undefined {
  return orderBy(
    [...members],
    [
      (member) => LAB_COPY_PRIORITY[member.kind],
      (member) => receivedAt(member.received),
      (member) => member.reference,
    ],
    ['asc', 'asc', 'asc']
  )[0]
}

// The state every copy of one accession within one participant should be in.
// A lone copy is not a group and carries nothing, which also clears markers a
// vanished sibling left behind. A copy is never marked as a copy of another
// document when that would take the result away from a reader:
// - one with an open review task stays in view so the work waiting on it does;
// - one in a different folder than the primary is readable by different people,
//   so it stays visible too;
// - a document in a folder is not chosen as primary while any copy outside every
//   folder exists, so readers without the folder still see the result.
export function planLabCopyGroup(
  accession: string,
  members: readonly LabCopyMember[]
): Map<string, LabCopyState> {
  const plan = new Map<string, LabCopyState>()
  const unfiled = members.filter((member) => !member.folder)
  const primary =
    members.length > 1
      ? choosePrimaryLabCopy(unfiled.length > 0 ? unfiled : members)
      : undefined
  for (const member of members) {
    if (!primary) {
      plan.set(member.reference, NO_LAB_COPY_GROUP)
      continue
    }
    const isCopy = member.reference !== primary.reference
    const pinned = isCopy && member.hasOpenReviewTask
    const sameAccess = member.folder === primary.folder
    plan.set(member.reference, {
      group: accession,
      copyOf: isCopy && !pinned && sameAccess ? primary.reference : undefined,
      pinned,
    })
  }
  return plan
}
